// Listens to the microphone and works out which of the song's chords is being
// played. Recognising any chord from audio is hard; picking one of the 4-8
// chords a song actually uses is much easier, and the order they appear in
// the song tells us which change is likely next.

import { chordTemplate } from './chords';
import { isMicrophoneSupported, microphone } from '../practice-dock/audio';

export const FFT_SIZE = 8192;
export const FRAME_MS = 50;

const MIN_FREQ = 75; // just under the low E string
const MAX_FREQ = 2000;
const BASS_MAX_FREQ = 220; // up to A3: the strings that carry a chord's bass note
const DYNAMIC_RANGE_DB = 50;
const TUNING_SPREAD = 0.3; // semitones; penalises peaks between notes

// Pitch-class profile from an analyser frame (dB per bin): 12 bins for the
// whole range, then 12 for the low register only, where the bass note of the
// chord tells G from G/B and Em7 from G. Only spectral peaks count, each
// placed by parabolic interpolation and weighted by how far it rises above
// the floor, which keeps noise and drum hits from smearing across all bins.
export function computeChroma(db, sampleRate, fftSize, out = new Float32Array(24)) {
    out.fill(0);
    const binHz = sampleRate / fftSize;
    const first = Math.max(2, Math.ceil(MIN_FREQ / binHz));
    const last = Math.min(db.length - 2, Math.floor(MAX_FREQ / binHz));
    let peak = -Infinity;
    for (let k = first; k <= last; k++) {
        if (db[k] > peak) peak = db[k];
    }
    const floor = peak - DYNAMIC_RANGE_DB;
    for (let k = first; k <= last; k++) {
        const b = db[k];
        if (b <= floor || b <= db[k - 1] || b < db[k + 1]) {
            continue;
        }
        const a = db[k - 1];
        const c = db[k + 1];
        const denom = a - 2 * b + c;
        const offset = denom === 0 ? 0 : 0.5 * (a - c) / denom;
        const freq = (k + offset) * binHz;
        const midi = 69 + 12 * Math.log2(freq / 440);
        const nearest = Math.round(midi);
        const deviation = midi - nearest;
        const weight = (b - floor) * Math.exp(-0.5 * (deviation / TUNING_SPREAD) ** 2);
        const pc = ((nearest % 12) + 12) % 12;
        out[pc] += weight;
        if (freq <= BASS_MAX_FREQ) {
            // Lower notes are more likely to be the bass than their overtones.
            out[12 + pc] += weight * (BASS_MAX_FREQ / freq);
        }
    }
    return out;
}

function normalise(vector) {
    let norm = 0;
    for (let i = 0; i < vector.length; i++) norm += vector[i] * vector[i];
    norm = Math.sqrt(norm);
    if (norm > 0) {
        for (let i = 0; i < vector.length; i++) vector[i] /= norm;
    }
    return norm;
}

function dot(a, b) {
    let sum = 0;
    for (let i = 0; i < a.length; i++) sum += a[i] * b[i];
    return sum;
}

// Tuned offline on synthesised strums: ~98% of frames right (99.9% on the
// bass note) with headphones, switching ~150ms after a chord change. Through
// speakers the backing track's own bass leaks into the microphone and the
// low register can't be trusted, so it's left out of the score there.
export const TRACKER_DEFAULTS = {
    stay: 0.9, // per-frame probability of staying on the same chord
    sharpness: 14, // how strongly a better template match wins
    bassWeight: 0.15, // share of the score that comes from the bass register
    hintBoost: 3, // extra transition weight towards the expected next chord
    switchAt: 0.7, // posterior needed to switch chords
};
export const SPEAKER_OPTIONS = { bassWeight: 0 };

// Hidden Markov model over the song's chords, filtered forward one frame at a
// time. Transitions that occur in the song are favoured over ones that don't,
// and an optional hint (the next chord at the player's position in the song)
// is favoured most.
export class ChordTracker {
    constructor(chords, sequence = [], options = {}) {
        Object.assign(this, TRACKER_DEFAULTS, options);
        this.chords = chords;
        this.templates = chords.map(chordTemplate);
        this.bassTemplates = chords.map(chord => {
            const template = new Float32Array(12);
            template[chord.bass] = 1;
            return template;
        });
        this.hint = null;

        const n = chords.length;
        const counts = Array.from({ length: n }, () => new Float32Array(n));
        for (let i = 1; i < sequence.length; i++) {
            if (sequence[i] !== sequence[i - 1]) {
                counts[sequence[i - 1]][sequence[i]] += 1;
            }
        }
        this.counts = counts;
        this.buildTransitions();
        this.reset();
    }

    buildTransitions() {
        const n = this.chords.length;
        this.transitions = this.counts.map((row, i) => {
            const weights = new Float32Array(n);
            if (n === 1) {
                weights[0] = 1;
                return weights;
            }
            let total = 0;
            for (let j = 0; j < n; j++) {
                if (j === i) continue;
                weights[j] = row[j] + 0.5 + (j === this.hint ? this.hintBoost : 0);
                total += weights[j];
            }
            for (let j = 0; j < n; j++) {
                weights[j] = j === i ? this.stay : (1 - this.stay) * weights[j] / total;
            }
            return weights;
        });
    }

    setHint(index) {
        if (index !== this.hint) {
            this.hint = index;
            this.buildTransitions();
        }
    }

    reset() {
        const n = this.chords.length;
        this.posterior = new Float32Array(n).fill(1 / n);
        this.current = null;
    }

    // Feed one chroma frame (see computeChroma); returns the index of the
    // chord currently believed to be played, or null before the first
    // confident decision.
    update(chroma) {
        const n = this.chords.length;
        if (n === 0) return this.current;
        const treble = Float32Array.from(chroma.subarray(0, 12));
        if (normalise(treble) === 0) return this.current;
        const bass = Float32Array.from(chroma.subarray(12, 24));
        const hasBass = normalise(bass) > 0;

        const scores = this.templates.map((template, j) => {
            const score = dot(template, treble);
            return hasBass ? (1 - this.bassWeight) * score + this.bassWeight * dot(this.bassTemplates[j], bass) : score;
        });
        const best = Math.max(...scores);
        const next = new Float32Array(n);
        let total = 0;
        for (let j = 0; j < n; j++) {
            let predicted = 0;
            for (let i = 0; i < n; i++) predicted += this.posterior[i] * this.transitions[i][j];
            next[j] = predicted * Math.exp(this.sharpness * (scores[j] - best));
            total += next[j];
        }
        for (let j = 0; j < n; j++) next[j] /= total;
        this.posterior = next;

        let top = 0;
        for (let j = 1; j < n; j++) if (next[j] > next[top]) top = j;
        if (this.current === null ? next[top] > 0.5 : top !== this.current && next[top] > this.switchAt) {
            this.current = top;
        }
        return this.current;
    }
}

// Where in the song the player is, from the chords heard so far: a hidden
// Markov model over the song's chord changes. Moving on to the next chord is
// by far the likeliest step, then staying put (the chord heard was misheard)
// and short skips; repeats and long jumps are possible but unlikely. The
// marker follows the single likeliest path through the song (Viterbi), so
// one misheard chord can't throw it across the song (a long jump needs a
// couple of chords in a row that agree with it), and in songs that repeat
// the same few chords it keeps to the copy you're in rather than wavering
// between identical verses.
const NEXT = 0.75;
const STAY = 0.12;
const SKIP = 0.08; // halves for each further chord skipped, up to 6 ahead
const BACK = 0.15; // spread over every earlier place (repeats)
const FAR = 0.05; // spread over every place further ahead

export class SongFollower {
    constructor(sequence, { misheard = 0.15 } = {}) {
        this.sequence = sequence;
        // Index into the sequence of each change of chord: two lines that both
        // start on Am are one stretch of Am to someone playing.
        this.runs = [];
        sequence.forEach((chord, i) => {
            if (i === 0 || sequence[i - 1] !== chord) this.runs.push(i);
        });
        this.chords = this.runs.map(i => sequence[i]);
        this.kinds = new Set(sequence).size;
        this.misheard = misheard;
        this.transitions = this.buildTransitions();
        this.belief = null;
        this.run = -1;
        this.position = -1;
        this.heard = null;
        this.lastChange = null;
        // A change that goes straight back to the chord before it within this
        // many seconds is a blip (see hear()). Set from the tempo.
        this.blipWindow = 0;
    }

    buildTransitions() {
        const n = this.runs.length;
        return Array.from({ length: n }, (_, from) => {
            const row = new Float64Array(n);
            let total = 0;
            for (let to = 0; to < n; to++) {
                // From the last chord the song can start over.
                const d = from === n - 1 && to === 0 ? 1 : to - from;
                if (d === 1) row[to] = NEXT;
                else if (d === 0) row[to] = STAY;
                else if (d > 1 && d <= 6) row[to] = SKIP * Math.pow(0.5, d - 2);
                else if (d < 0) row[to] = BACK / n;
                else row[to] = FAR / n;
                total += row[to];
            }
            for (let to = 0; to < n; to++) row[to] /= total;
            return row;
        });
    }

    // A chord change was heard at `time` (seconds); returns the position
    // (index into the sequence) to mark.
    hear(chordIndex, time = null) {
        const n = this.runs.length;
        if (n === 0) return -1;
        // Am → G → Am within a beat or so isn't the song moving on: it's the
        // listener mishearing a strum, or a passing chord. Undo the step.
        const last = this.lastChange;
        if (last && time !== null && chordIndex === last.from && time - last.time < this.blipWindow) {
            Object.assign(this, last.before);
            this.heard = chordIndex;
            this.lastChange = null;
            return this.position;
        }
        this.lastChange = { from: this.heard, time, before: { belief: this.belief, run: this.run, position: this.position } };
        this.heard = chordIndex;
        const prior = new Float64Array(n);
        if (this.belief) {
            for (let from = 0; from < n; from++) {
                const weight = this.belief[from];
                if (weight < 1e-12) continue;
                const row = this.transitions[from];
                for (let to = 0; to < n; to++) {
                    const score = weight * row[to];
                    if (score > prior[to]) prior[to] = score;
                }
            }
        } else {
            // Players usually start at the top, but could start anywhere.
            prior.fill(0.5 / n);
            prior[0] += 0.5;
        }
        const wrong = this.misheard / Math.max(1, this.kinds - 1);
        let best = 0;
        for (let q = 0; q < n; q++) {
            prior[q] *= this.chords[q] === chordIndex ? 1 - this.misheard : wrong;
            if (prior[q] > prior[best]) best = q;
        }
        // Scale so the best path scores 1, keeping the numbers from underflowing.
        const top = prior[best];
        for (let q = 0; q < n; q++) prior[q] /= top;
        this.belief = prior;
        this.run = best;
        this.position = this.runs[best];
        return this.position;
    }

    // Tap mode: step through the chord changes; returns the new position.
    step(delta) {
        const n = this.runs.length;
        if (n === 0) return -1;
        this.run = this.run === -1 && delta > 0 ? 0 : ((this.run + delta) % n + n) % n;
        this.heard = this.chords[this.run];
        this.lastChange = null;
        this.belief = new Float64Array(n);
        this.belief[this.run] = 1;
        this.position = this.runs[this.run];
        return this.position;
    }

    current() {
        return this.run >= 0 ? this.chords[this.run] : null;
    }

    expectedNext() {
        const n = this.runs.length;
        if (this.run < 0 || n < 2) return null;
        const next = this.chords[(this.run + 1) % n];
        return next === this.chords[this.run] ? null : next;
    }
}

// Browser side: the dock's shared microphone → AnalyserNode, sampled every
// FRAME_MS.
export default class MicInput {
    constructor(audioContext, onFrame) {
        this.ctx = audioContext;
        this.onFrame = onFrame;
        this.noiseFloor = null;
    }

    static isSupported() {
        return isMicrophoneSupported();
    }

    async start({ headphones = false } = {}) {
        // Echo cancellation stays on when the dock's sound comes out of
        // speakers, so the listener doesn't hear the backing track's bass.
        await microphone.acquire(this, { echoCancellation: !headphones });
        this.analyser = this.ctx.createAnalyser();
        this.analyser.fftSize = FFT_SIZE;
        this.analyser.smoothingTimeConstant = 0;
        microphone.connect(this.analyser);
        this.freq = new Float32Array(this.analyser.frequencyBinCount);
        this.time = new Float32Array(2048);
        this.chroma = new Float32Array(24);
        this.timer = setInterval(() => this.tick(), FRAME_MS);
    }

    tick() {
        this.analyser.getFloatTimeDomainData(this.time);
        let sum = 0;
        for (let i = 0; i < this.time.length; i++) sum += this.time[i] * this.time[i];
        const rms = Math.sqrt(sum / this.time.length);

        // Slowly follow the quietest level seen as the room's noise floor.
        if (this.noiseFloor === null || rms < this.noiseFloor) {
            this.noiseFloor = rms;
        } else {
            this.noiseFloor += (rms - this.noiseFloor) * 0.002;
        }
        const active = rms > Math.max(0.004, this.noiseFloor * 3);

        if (active) {
            this.analyser.getFloatFrequencyData(this.freq);
            computeChroma(this.freq, this.ctx.sampleRate, FFT_SIZE, this.chroma);
        }
        this.onFrame({ rms, active, chroma: this.chroma });
    }

    stop() {
        clearInterval(this.timer);
        if (this.analyser) microphone.disconnect(this.analyser);
        this.analyser = null;
        microphone.release(this);
    }
}
